/**
 * TransactionsGrid - Column definitions and grid configuration for the Project Transactions table.
 *
 * Architecture (following EBTimeEntry's TimesheetReportGrid pattern):
 *   ProjectTransactionsPage (data fetching, filters, pagination state)
 *     \u2514\u2500 TransactionsGrid (column definitions, cell renderers)  \u2190 this file
 *         \u2514\u2500 DataTableEnhanced (generic table rendering)
 *
 * All cell rendering logic that was previously in the monolithic renderCellValue
 * switch statement in project-transactions.tsx is now expressed as TanStack ColumnDef cell renderers.
 */

import { useMemo } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import { Badge } from '@/components/ui/badge';
import { SensitiveData } from '@/components/sensitive-data';
import {
  DataTableEnhanced,
  columnDefinitionsToVisibility,
  type ColumnSort,
} from '@/components/ui/data-table-enhanced';
import { formatCurrency, formatDate } from '@/lib/utils';
import type { ColumnDefinition } from '@/components/column-configurator-drawer';

// ── Types ────────────────────────────────────────────────────────────────

type TransactionType =
  | 'NotSet'
  | 'Timesheet'
  | 'Invoice'
  | 'CreditNote'
  | 'Sales'
  | 'Journal'
  | 'TaxJournal'
  | 'DebitAdjustment'
  | 'CreditAdjustment'
  | 'ReceiptPayment'
  | 'Miscellaneous'
  | 'CurrencyTransfer'
  | 'StockManagement';

type TransactionOrigin =
  | 'NotSet'
  | 'Costing'
  | 'PurchaseLedger'
  | 'SalesLedger'
  | 'NominalLedger'
  | 'Stock';

type PostType = 'C' | 'I' | 'S';

/** The CostingTransactionHeader interface used by the existing page component */
export interface CostingTransactionHeader {
  projectCode: string;
  projectName: string;
  date: string;
  transactionType: TransactionType;
  origin: TransactionOrigin;
  detail: string;
  headerRef: string;
  internalRef: number;
  accountCode: string;
  accountName: string;
  costCentreCode: string;
  costCentreName: string;
  invoiceAccountCode: string;
  invoiceAccountName: string;
  supplierAccountCode: string;
  resourceCode: string;
  resourceName: string;
  employeeCode: string;
  quantity: number;
  price: number;
  value: number;
  uplift: number;
  upliftValue: number;
  priceWithUplift: number;
  actualSales: number;
  actualCost: number;
  currencyCode: string;
  postType: PostType;
  projectStatus: string;
  batched: boolean;
  isInvoiced: boolean;
  periodNumber: number;
  yearLabel: string;
  weekNo: number;
  costingSortKey: string;
  salesOrderNumber: number;
  purchaseOrderNumber: string;
  batchRef: string;
  enteredBy: string;
  dateEntered: string;
  costingUserField1: string;
  costingUserField2: string;
  costingUserField3: string;
  costingUserField4: string;
  costingUserField5: string;
  costingUserField6: string;
  userDate1: string;
  userDate2: string;
  userChar1: string;
  userChar2: string;
  userChar3: string;
  userChar4: string;
  userFlag1: boolean;
  userFlag2: boolean;
  userNumber1: number;
  userNumber2: number;
  userKey1: string;
  userKey2: string;
  userKey3: string;
  stockCode: string;
  stockName: string;
  analysisCode: string;
  analysisName: string;
  nominalCode: string;
}

export interface TransactionsGridProps {
  /** Pre-sorted/paginated transaction data to render */
  data: CostingTransactionHeader[];
  /** Column definitions from ColumnConfiguratorDrawer (controls visibility & order) */
  columnDefinitions: ColumnDefinition[];
  /** Multi-column sort state */
  columnSorts: ColumnSort[];
  /** Callback when sort state changes */
  onColumnSortChange: (sorts: ColumnSort[]) => void;
  /** Show loading skeleton */
  loading?: boolean;
  /** Show transition overlay */
  isTransitioning?: boolean;
  /** Number of skeleton rows when loading */
  pageSize?: number;
  /** Current search/filter state for empty message */
  hasActiveFilters?: boolean;
  /** Optional pagination component (parent renders its own because of cursor-based live mode) */
  paginationContent?: React.ReactNode;
  /** Optional className forwarded to DataTableEnhanced */
  className?: string;
}

// ── Badge helpers ─────────────────────────────────────────────────────────

function getTransactionTypeBadge(type: TransactionType) {
  const variants: Record<TransactionType, 'default' | 'secondary' | 'success' | 'warning' | 'destructive'> = {
    NotSet: 'secondary',
    Timesheet: 'default',
    Invoice: 'secondary',
    CreditNote: 'warning',
    Sales: 'success',
    Journal: 'secondary',
    TaxJournal: 'secondary',
    DebitAdjustment: 'destructive',
    CreditAdjustment: 'success',
    ReceiptPayment: 'secondary',
    Miscellaneous: 'warning',
    CurrencyTransfer: 'secondary',
    StockManagement: 'secondary',
  };
  return <Badge variant={variants[type] ?? 'secondary'}>{type}</Badge>;
}

function getOriginBadge(origin: TransactionOrigin) {
  return <Badge variant="outline">{origin}</Badge>;
}

// ── Column builder ───────────────────────────────────────────────────────

/**
 * Build the full set of TanStack column definitions for the Transactions grid.
 * Each column ID matches the ColumnDefinition IDs from the page component.
 * Cell renderers replicate the exact same output as the old renderCellValue switch.
 */
function buildTransactionColumns(): ColumnDef<CostingTransactionHeader, unknown>[] {
  return [
    {
      id: 'projectCode',
      accessorFn: (row) => row.projectCode,
      header: 'Project Code',
      cell: ({ row }) => row.original.projectCode,
    },
    {
      id: 'projectName',
      accessorFn: (row) => row.projectName,
      header: 'Project Name',
      cell: ({ row }) => (
        <span className="font-medium">{row.original.projectName}</span>
      ),
    },
    {
      id: 'accountCode',
      accessorFn: (row) => row.accountCode,
      header: 'Account Code',
      cell: ({ row }) => row.original.accountCode,
    },
    {
      id: 'accountName',
      accessorFn: (row) => row.accountName,
      header: 'Account Name',
      cell: ({ row }) => row.original.accountName,
    },
    {
      id: 'date',
      accessorFn: (row) => row.date,
      header: 'Date',
      cell: ({ row }) => formatDate(row.original.date),
    },
    {
      id: 'transactionType',
      accessorFn: (row) => row.transactionType,
      header: 'Type',
      cell: ({ row }) => getTransactionTypeBadge(row.original.transactionType),
    },
    {
      id: 'origin',
      accessorFn: (row) => row.origin,
      header: 'Origin',
      cell: ({ row }) => getOriginBadge(row.original.origin),
    },
    {
      id: 'resourceName',
      accessorFn: (row) => row.resourceName,
      header: 'Resource',
      cell: ({ row }) => row.original.resourceName || '-',
    },
    {
      id: 'quantity',
      accessorFn: (row) => row.quantity,
      header: 'Quantity',
      meta: { align: 'right' },
      cell: ({ row }) => (
        <SensitiveData>{row.original.quantity}</SensitiveData>
      ),
    },
    {
      id: 'price',
      accessorFn: (row) => row.price,
      header: 'Price',
      meta: { align: 'right' },
      cell: ({ row }) => (
        <SensitiveData>{formatCurrency(row.original.price)}</SensitiveData>
      ),
    },
    {
      id: 'value',
      accessorFn: (row) => row.value,
      header: 'Value',
      meta: { align: 'right' },
      cell: ({ row }) => (
        <SensitiveData>{formatCurrency(row.original.value)}</SensitiveData>
      ),
    },
    {
      id: 'batched',
      accessorFn: (row) => (row.batched ? 1 : 0),
      header: 'Batched',
      cell: ({ row }) => (row.original.batched ? '\u2713' : ''),
    },
    // ── Financial columns (additional) ────────────────────────────────
    {
      id: 'uplift',
      accessorFn: (row) => row.uplift,
      header: 'Uplift %',
      meta: { align: 'right' },
      cell: ({ row }) => (
        <SensitiveData>{row.original.uplift}%</SensitiveData>
      ),
    },
    {
      id: 'upliftValue',
      accessorFn: (row) => row.upliftValue,
      header: 'Uplift Value',
      meta: { align: 'right' },
      cell: ({ row }) => (
        <SensitiveData>{formatCurrency(row.original.upliftValue)}</SensitiveData>
      ),
    },
    {
      id: 'priceWithUplift',
      accessorFn: (row) => row.priceWithUplift,
      header: 'Price + Uplift',
      meta: { align: 'right' },
      cell: ({ row }) => (
        <SensitiveData>{formatCurrency(row.original.priceWithUplift)}</SensitiveData>
      ),
    },
    {
      id: 'valueWithUplift',
      accessorFn: (row) => row.value + row.upliftValue,
      header: 'Value + Uplift',
      meta: { align: 'right' },
      cell: ({ row }) => (
        <SensitiveData>
          {formatCurrency(row.original.value + row.original.upliftValue)}
        </SensitiveData>
      ),
    },
    {
      id: 'actualSales',
      accessorFn: (row) => row.actualSales,
      header: 'Actual Sales',
      meta: { align: 'right' },
      cell: ({ row }) => (
        <SensitiveData>{formatCurrency(row.original.actualSales)}</SensitiveData>
      ),
    },
    {
      id: 'actualCost',
      accessorFn: (row) => row.actualCost,
      header: 'Actual Cost',
      meta: { align: 'right' },
      cell: ({ row }) => (
        <SensitiveData>{formatCurrency(row.original.actualCost)}</SensitiveData>
      ),
    },
    {
      id: 'currencyCode',
      accessorFn: (row) => row.currencyCode,
      header: 'Currency',
      cell: ({ row }) => row.original.currencyCode,
    },
    // ── Resource columns ──────────────────────────────────────────────
    {
      id: 'resourceCode',
      accessorFn: (row) => row.resourceCode,
      header: 'Resource Code',
      cell: ({ row }) => row.original.resourceCode || '-',
    },
    {
      id: 'employeeCode',
      accessorFn: (row) => row.employeeCode,
      header: 'Employee Code',
      cell: ({ row }) => row.original.employeeCode || '-',
    },
    // ── Account columns ───────────────────────────────────────────────
    {
      id: 'invoiceAccountCode',
      accessorFn: (row) => row.invoiceAccountCode,
      header: 'Invoice Account Code',
      cell: ({ row }) => row.original.invoiceAccountCode || '-',
    },
    {
      id: 'invoiceAccountName',
      accessorFn: (row) => row.invoiceAccountName,
      header: 'Invoice Account Name',
      cell: ({ row }) => row.original.invoiceAccountName || '-',
    },
    {
      id: 'supplierAccountCode',
      accessorFn: (row) => row.supplierAccountCode,
      header: 'Supplier Code',
      cell: ({ row }) => row.original.supplierAccountCode || '-',
    },
    // ── Status columns ────────────────────────────────────────────────
    {
      id: 'postType',
      accessorFn: (row) => row.postType,
      header: 'Post Type',
      cell: ({ row }) => <Badge variant="outline">{row.original.postType}</Badge>,
    },
    {
      id: 'projectStatus',
      accessorFn: (row) => row.projectStatus,
      header: 'Project Status',
      cell: ({ row }) => <Badge variant="default">{row.original.projectStatus}</Badge>,
    },
    {
      id: 'isInvoiced',
      accessorFn: (row) => (row.isInvoiced ? 1 : 0),
      header: 'Invoiced',
      cell: ({ row }) => (row.original.isInvoiced ? '\u2713' : ''),
    },
    // ── Period columns ────────────────────────────────────────────────
    {
      id: 'periodNumber',
      accessorFn: (row) => row.periodNumber,
      header: 'Period',
      meta: { align: 'right' },
      cell: ({ row }) => row.original.periodNumber,
    },
    {
      id: 'yearLabel',
      accessorFn: (row) => row.yearLabel,
      header: 'Year',
      cell: ({ row }) => row.original.yearLabel,
    },
    {
      id: 'weekNo',
      accessorFn: (row) => row.weekNo,
      header: 'Week',
      meta: { align: 'right' },
      cell: ({ row }) => row.original.weekNo,
    },
    // ── Reference columns ─────────────────────────────────────────────
    {
      id: 'detail',
      accessorFn: (row) => row.detail,
      header: 'Detail',
      cell: ({ row }) => row.original.detail,
    },
    {
      id: 'headerRef',
      accessorFn: (row) => row.headerRef,
      header: 'Header Ref',
      cell: ({ row }) => row.original.headerRef,
    },
    {
      id: 'internalRef',
      accessorFn: (row) => row.internalRef,
      header: 'Internal Ref',
      meta: { align: 'right' },
      cell: ({ row }) => row.original.internalRef,
    },
    {
      id: 'costCentreCode',
      accessorFn: (row) => row.costCentreCode,
      header: 'Cost Centre Code',
      cell: ({ row }) => row.original.costCentreCode,
    },
    {
      id: 'costCentreName',
      accessorFn: (row) => row.costCentreName,
      header: 'Cost Centre',
      cell: ({ row }) => row.original.costCentreName,
    },
    {
      id: 'costingSortKey',
      accessorFn: (row) => row.costingSortKey,
      header: 'Costing Sort Key',
      cell: ({ row }) => row.original.costingSortKey,
    },
    {
      id: 'salesOrderNumber',
      accessorFn: (row) => row.salesOrderNumber,
      header: 'Sales Order',
      meta: { align: 'right' },
      cell: ({ row }) => row.original.salesOrderNumber || '-',
    },
    {
      id: 'purchaseOrderNumber',
      accessorFn: (row) => row.purchaseOrderNumber,
      header: 'Purchase Order',
      cell: ({ row }) => row.original.purchaseOrderNumber || '-',
    },
    {
      id: 'batchRef',
      accessorFn: (row) => row.batchRef,
      header: 'Batch Ref',
      cell: ({ row }) => row.original.batchRef || '-',
    },
    {
      id: 'enteredBy',
      accessorFn: (row) => row.enteredBy,
      header: 'Entered By',
      cell: ({ row }) => row.original.enteredBy,
    },
    {
      id: 'dateEntered',
      accessorFn: (row) => row.dateEntered,
      header: 'Date Entered',
      cell: ({ row }) => formatDate(row.original.dateEntered),
    },
    {
      id: 'stockCode',
      accessorFn: (row) => row.stockCode,
      header: 'Stock Code',
      cell: ({ row }) => row.original.stockCode || '-',
    },
    {
      id: 'stockName',
      accessorFn: (row) => row.stockName,
      header: 'Stock Name',
      cell: ({ row }) => row.original.stockName || '-',
    },
    {
      id: 'analysisCode',
      accessorFn: (row) => row.analysisCode,
      header: 'Analysis Code',
      cell: ({ row }) => row.original.analysisCode,
    },
    {
      id: 'analysisName',
      accessorFn: (row) => row.analysisName,
      header: 'Analysis',
      cell: ({ row }) => row.original.analysisName,
    },
    {
      id: 'nominalCode',
      accessorFn: (row) => row.nominalCode,
      header: 'Nominal Code',
      cell: ({ row }) => row.original.nominalCode,
    },
  ];
}

// ── Component ────────────────────────────────────────────────────────────

export function TransactionsGrid({
  data,
  columnDefinitions,
  columnSorts,
  onColumnSortChange,
  loading = false,
  isTransitioning = false,
  pageSize = 25,
  hasActiveFilters = false,
  paginationContent,
  className,
}: TransactionsGridProps) {
  // Build full column set with cell renderers (stable - no dependencies)
  const allColumns = useMemo(() => buildTransactionColumns(), []);

  // Filter and order columns to match the visible ColumnDefinitions
  const visibleColumnDefs = useMemo(() => {
    const visibleDefs = columnDefinitions
      .filter((cd) => cd.visible)
      .sort((a, b) => a.order - b.order);

    const columnMap = new Map(allColumns.map((c) => [c.id, c]));
    return visibleDefs
      .map((cd) => columnMap.get(cd.id))
      .filter((c): c is ColumnDef<CostingTransactionHeader, unknown> => c !== undefined);
  }, [columnDefinitions, allColumns]);

  // Build TanStack visibility state from ColumnDefinitions
  const columnVisibility = useMemo(
    () => columnDefinitionsToVisibility(columnDefinitions),
    [columnDefinitions],
  );

  // Empty state messages
  const emptyMessage = hasActiveFilters ? (
    <div className="space-y-2">
      <p className="text-lg font-medium">No transactions found</p>
      <p className="text-sm">Try adjusting your filters or search term</p>
    </div>
  ) : (
    <div className="space-y-2">
      <p className="text-lg font-medium">No transactions</p>
      <p className="text-sm">No transaction data available</p>
    </div>
  );

  return (
    <DataTableEnhanced<CostingTransactionHeader, unknown>
      columns={visibleColumnDefs}
      data={data}
      columnVisibility={columnVisibility}
      columnSorts={columnSorts}
      onColumnSortChange={onColumnSortChange}
      loading={loading}
      loadingRowCount={pageSize}
      isTransitioning={isTransitioning}
      emptyMessage={emptyMessage}
      dataTour="transactions-table"
      paginationContent={paginationContent}
      className={className}
    />
  );
}
